/**
 * Build per-Fall runConfig: first-Fall countdown + rotating FALL_PHASE_PROFILES by cycleIndex.
 */

import * as C from "../config/constants.js";

/**
 * @param {object} [runConfig]
 * @returns {number}
 */
export function getFallCountdownTickMs(runConfig) {
  var cfg = runConfig || {};
  var tickMs = cfg.FALL_COUNTDOWN_TICK_MS != null
    ? Number(cfg.FALL_COUNTDOWN_TICK_MS)
    : (C.RUN_CONFIG && C.RUN_CONFIG.FALL_COUNTDOWN_TICK_MS != null
      ? Number(C.RUN_CONFIG.FALL_COUNTDOWN_TICK_MS)
      : 1000);
  if (!isFinite(tickMs) || tickMs <= 0) return 1000;
  return tickMs;
}

/**
 * @param {object} [runConfig]
 * @returns {number}
 */
export function getFallCountdownTickSec(runConfig) {
  return getFallCountdownTickMs(runConfig) / 1000;
}

var PROFILE_KEYS = [
  "BOULDER_COUNT_MIN",
  "BOULDER_COUNT_MAX",
  "HAZARD_COUNT_MIN",
  "HAZARD_COUNT_MAX",
  "GEM_CLUSTER_COUNT_MIN",
  "GEM_CLUSTER_COUNT_MAX",
  "BONUS_COUNT_MIN",
  "BONUS_COUNT_MAX",
  "MERGE_PICKUP_COUNT_MIN",
  "MERGE_PICKUP_COUNT_MAX",
];

/**
 * Boulder count multiplier for the first Fall day cycles in a run.
 * cycleIndex 0 = day 1 (none), 1 = day 2 full count, 2–3 = days 3–4 at 25%, 4+ = day 5+ full profile counts.
 * @param {number} cycleIndex Completed Fall cycles before this Fall (0-based).
 * @returns {number} 0–1 scale applied to profile boulder min/max.
 */
export function getFallBoulderRampScale(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  if (idx === 0) return 0;
  if (idx === 1) return 1;
  if (idx <= 3) return 0.25;
  return 1;
}

/**
 * Merge pickup count multiplier for each Fall day in a run.
 * cycleIndex 0 = day 1 (80%), 1 = 90%, 2+ = 100%.
 * @param {number} cycleIndex Completed Fall cycles before this Fall (0-based).
 * @returns {number} scale applied to merge pickup min/max.
 */
export function getFallMergePickupRampScale(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  var ramp = C.FALL_MERGE_PICKUP_RAMP || [0.8, 0.9, 1.0];
  if (idx >= ramp.length) return ramp[ramp.length - 1];
  var scale = Number(ramp[idx]);
  return isFinite(scale) && scale > 0 ? scale : 1;
}

/**
 * Fall gravity multiplier for each Fall day in a run.
 * cycleIndex 0 = day 1 (60%), +10% per day, 4+ = day 5+ (100%).
 * @param {number} cycleIndex Completed Fall cycles before this Fall (0-based).
 * @returns {number} scale applied to FALL_GRAVITY.
 */
export function getFallGravityRampScale(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  var ramp = C.FALL_GRAVITY_DAY_RAMP || [1];
  if (idx >= ramp.length) return ramp[ramp.length - 1];
  var scale = Number(ramp[idx]);
  return isFinite(scale) && scale > 0 ? scale : 1;
}

/** Days 1–2 (`cycleIndex` 0–1): no shield pickups. */
export function isFallShieldSuppressionDay(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  return idx <= 1;
}

/** Day 2 (`cycleIndex` 1): stop boulder spawns after the first HP hit this Fall. */
export function isFallFirstHitBoulderSuppressionDay(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  return idx === 1;
}

/** Days 1–3 (`cycleIndex` 0–2): skip boulder layout/spawn when flask HP is ≤ 1. */
export function isFallLowHpBoulderSuppressionDay(cycleIndex) {
  var idx = Math.max(0, Math.floor(Number(cycleIndex) || 0));
  return idx <= 2;
}

/**
 * Mid-Fall boulder gate used by `FallPhase._spawnDue`.
 * Day 2: skip after the first HP hit this Fall, or immediately at 1 HP.
 * Days 1–3: also skip while HP is ≤ 1.
 * @param {object} [runConfig]
 * @param {{ hp?: number }|null} [player]
 * @param {number|null} [fallStartHp] HP when this Fall started.
 */
export function shouldSkipFallBoulderSpawn(runConfig, player, fallStartHp) {
  if (!player) return false;
  var hp = Number(player.hp);
  if (!isFinite(hp)) return false;
  var cfg = runConfig || {};
  if (cfg.fallSuppressBouldersAfterFirstHit) {
    var startHp = fallStartHp != null ? Number(fallStartHp) : hp;
    if (isFinite(startHp) && hp < startHp) return true;
    if (hp <= 1) return true;
  }
  if (cfg.fallSuppressBouldersAtLowHp && hp <= 1) return true;
  return false;
}

/**
 * Mid-Fall shield gate used by `FallPhase._spawnDue`.
 * Days 1–2 suppress all shield pickups; later days still skip while a shield is active.
 * @param {object} [runConfig]
 * @param {{ hasShield?: boolean }|null} [player]
 */
export function shouldSkipFallBonusSpawn(runConfig, player) {
  var cfg = runConfig || {};
  if (cfg.fallSuppressShields) return true;
  return !!(player && player.hasShield);
}

function applyProfileOverrides(runConfig, profile) {
  if (!profile) return;
  for (var i = 0; i < PROFILE_KEYS.length; i++) {
    var key = PROFILE_KEYS[i];
    if (profile[key] != null) runConfig[key] = profile[key];
  }
}

function scaleCountRange(min, max, scale) {
  min = Math.max(0, Math.floor(Number(min) || 0));
  max = Math.max(min, Math.floor(Number(max) || 0));
  if (scale <= 0) return { min: 0, max: 0 };
  var scaledMin = Math.max(0, Math.round(min * scale));
  var scaledMax = Math.max(scaledMin, Math.round(max * scale));
  return { min: scaledMin, max: scaledMax };
}

function applyBoulderRamp(runConfig, cycleIndex) {
  var scale = getFallBoulderRampScale(cycleIndex);
  var boulder = scaleCountRange(runConfig.BOULDER_COUNT_MIN, runConfig.BOULDER_COUNT_MAX, scale);
  runConfig.BOULDER_COUNT_MIN = boulder.min;
  runConfig.BOULDER_COUNT_MAX = boulder.max;
  if (scale <= 0 || runConfig.HAZARD_COUNT_MIN != null || runConfig.HAZARD_COUNT_MAX != null) {
    var hazard = scaleCountRange(
      runConfig.HAZARD_COUNT_MIN || 0,
      runConfig.HAZARD_COUNT_MAX || 0,
      scale
    );
    runConfig.HAZARD_COUNT_MIN = hazard.min;
    runConfig.HAZARD_COUNT_MAX = hazard.max;
  }
  runConfig.fallBoulderRampScale = scale;
}

function applyMergePickupRamp(runConfig, cycleIndex) {
  var scale = getFallMergePickupRampScale(cycleIndex);
  var merge = scaleCountRange(
    runConfig.MERGE_PICKUP_COUNT_MIN,
    runConfig.MERGE_PICKUP_COUNT_MAX,
    scale
  );
  runConfig.MERGE_PICKUP_COUNT_MIN = merge.min;
  runConfig.MERGE_PICKUP_COUNT_MAX = merge.max;
  runConfig.fallMergePickupRampScale = scale;
}

function applyGravityRamp(runConfig, cycleIndex) {
  var scale = getFallGravityRampScale(cycleIndex);
  var base = runConfig.FALL_GRAVITY != null ? Number(runConfig.FALL_GRAVITY) : C.FALL_GRAVITY;
  if (!isFinite(base) || base <= 0) base = C.FALL_GRAVITY;
  runConfig.FALL_GRAVITY = base * scale;
  runConfig.fallGravityRampScale = scale;
}

function suppressBouldersForLowHp(runConfig) {
  runConfig.BOULDER_COUNT_MIN = 0;
  runConfig.BOULDER_COUNT_MAX = 0;
  runConfig.HAZARD_COUNT_MIN = 0;
  runConfig.HAZARD_COUNT_MAX = 0;
}

/** Shield bonuses are only meaningful when boulders can spawn. */
function suppressBonusesWithoutBoulders(runConfig) {
  var boulderMax = Math.max(0, Math.floor(Number(runConfig.BOULDER_COUNT_MAX) || 0));
  var boulderMin = Math.max(0, Math.floor(Number(runConfig.BOULDER_COUNT_MIN) || 0));
  var hazardMax = Math.max(0, Math.floor(Number(runConfig.HAZARD_COUNT_MAX) || 0));
  var hazardMin = Math.max(0, Math.floor(Number(runConfig.HAZARD_COUNT_MIN) || 0));
  if (boulderMax === 0 && boulderMin === 0 && hazardMax === 0 && hazardMin === 0) {
    runConfig.BONUS_COUNT_MIN = 0;
    runConfig.BONUS_COUNT_MAX = 0;
  }
}

/**
 * @param {Object} [options]
 * @param {number} [options.cycleIndex] Completed Fall cycles in the current run (0 = first Fall).
 * @param {number} [options.playerHp] Flask HP at Fall start — days 1–3 zero boulder layout when <= 1.
 * @param {string} [options.fallStartSource] Analytics source (`menu` / `play_again` / `continue` / `resume`).
 * @returns {Object} Mutable runConfig for one Fall phase.
 */
export function buildFallRunConfig(options) {
  var opts = options || {};
  var cycleIndex = Math.max(0, Math.floor(Number(opts.cycleIndex) || 0));
  var runConfig = Object.assign({}, C.RUN_CONFIG || {});
  var profiles = C.FALL_PHASE_PROFILES || [];

  if (cycleIndex === 0) {
    // First Fall of each run: 3 s countdown, tutorial loot (no boulders, no shields).
    if (profiles.length > 0) {
      applyProfileOverrides(runConfig, profiles[0]);
    }
    runConfig.fallPhaseProfileId = "tutorial";
  } else {
    // Later Falls in the same run skip the digit countdown — except checkpoint resume,
    // which needs a short silent hold so spawns do not run behind the boot loader.
    runConfig.RUN_COUNTDOWN_SECONDS = 0;
    if (opts.fallStartSource === "resume") {
      var resumeDelay = runConfig.FALL_RESUME_SPAWN_DELAY_SECONDS != null
        ? Number(runConfig.FALL_RESUME_SPAWN_DELAY_SECONDS)
        : 1;
      if (isFinite(resumeDelay) && resumeDelay > 0) {
        runConfig.RUN_COUNTDOWN_SECONDS = resumeDelay;
        runConfig.fallCountdownSilent = true;
      }
    }

    if (profiles.length > 0) {
      var profileIdx = (cycleIndex - 1) % profiles.length;
      var profile = profiles[profileIdx];
      applyProfileOverrides(runConfig, profile);
      runConfig.fallPhaseProfileId = profile && profile.id ? profile.id : ("profile_" + profileIdx);
    } else {
      runConfig.fallPhaseProfileId = "default";
    }
  }

  applyBoulderRamp(runConfig, cycleIndex);
  applyMergePickupRamp(runConfig, cycleIndex);
  applyGravityRamp(runConfig, cycleIndex);

  runConfig.fallSuppressShields = isFallShieldSuppressionDay(cycleIndex);
  if (runConfig.fallSuppressShields) {
    runConfig.BONUS_COUNT_MIN = 0;
    runConfig.BONUS_COUNT_MAX = 0;
  }

  runConfig.cycleIndex = cycleIndex;
  runConfig.fallSuppressBouldersAfterFirstHit = isFallFirstHitBoulderSuppressionDay(cycleIndex);
  runConfig.fallSuppressBouldersAtLowHp = isFallLowHpBoulderSuppressionDay(cycleIndex);
  var playerHp = opts.playerHp != null ? Math.floor(Number(opts.playerHp) || 0) : null;
  var suppressBouldersAtStart = playerHp != null && playerHp <= 1 && (
    runConfig.fallSuppressBouldersAfterFirstHit || runConfig.fallSuppressBouldersAtLowHp
  );
  if (suppressBouldersAtStart) {
    suppressBouldersForLowHp(runConfig);
    runConfig.fallBouldersSuppressedForLowHp = true;
  }

  suppressBonusesWithoutBoulders(runConfig);
  applyDevDurationScale(runConfig, opts.durationScale);
  return runConfig;
}

/**
 * Dev-only Fall speed-up. Spawn times are `progress × RUN_DURATION` (see run/layout.js),
 * so scaling the duration compresses the whole spawn table rather than truncating it —
 * the same objects still fall, just denser. Countdown is scaled too so a 25% run does not
 * spend most of its wall time on "3, 2, 1". No-op unless a scale below 1 is passed in,
 * and `Game` only passes one in DEV builds.
 *
 * @param {Record<string, any>} runConfig
 * @param {number | null | undefined} scale
 */
function applyDevDurationScale(runConfig, scale) {
  var s = Number(scale);
  if (!isFinite(s) || s <= 0 || s >= 1) return;
  var base = Number(runConfig.RUN_DURATION);
  if (!isFinite(base) || base <= 0) return;
  runConfig.RUN_DURATION = Math.max(1, base * s);
  var countdown = Number(runConfig.RUN_COUNTDOWN_SECONDS);
  if (isFinite(countdown) && countdown > 0) {
    runConfig.RUN_COUNTDOWN_SECONDS = Math.max(1, Math.round(countdown * s));
  }
  runConfig.devDurationScale = s;
}
